import { useMutation, useQuery } from '@apollo/client'
import { Button, Grid } from '@mui/material'
import ToggleEditMultiSelectField from 'candidate/components/ToggleEditFormFields/ToggleEditMultiSelectField'
import { GetCandidateIndustryPreferenceOptions } from 'candidate/graphql/recruitingPreferences.gql'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useToast from 'shared/components/Toast/useToast'
import { UpdateCandidateIndustryPreferences } from 'shared/graphql/candidatePreferences.gql'
import { DropdownOption } from 'shared/models/dropdownOption'
import { EditButtonContainer } from 'shared/style/styled'
import PreferenceSectionHeader from './PreferenceSectionHeader'
import { RecruitingFormComponentProps } from './preference.types'

const Industry: React.FC<RecruitingFormComponentProps> = ({
    candidate,
    handleEditArray,
    editArray,
    sectionName,
    setUnsavedChangesExist,
    refetch,
}) => {
    const [mutate] = useMutation(UpdateCandidateIndustryPreferences)
    const toast = useToast()
    const industryPreferences = useMemo(
        () => candidate.industryPreferences ?? [],
        [candidate]
    )

    const [primaryIndustries, setPrimaryIndustries] = useState<
        DropdownOption[]
    >(
        industryPreferences
            .filter(({ order }) => order === 1)
            .map((pref) => pref.preferenceIndustry)
    )
    const [secondaryIndustries, setSecondaryIndustries] = useState<
        DropdownOption[]
    >(
        industryPreferences
            .filter(({ order }) => order === 2)
            .map((pref) => pref.preferenceIndustry)
    )
    const [tertiaryIndustries, setTertiaryIndustries] = useState<
        DropdownOption[]
    >(
        industryPreferences
            .filter(({ order }) => order === 3)
            .map((pref) => pref.preferenceIndustry)
    )

    const IndustriesOptions = useQuery(GetCandidateIndustryPreferenceOptions)

    const resetForm = useCallback(() => {
        setPrimaryIndustries(
            industryPreferences
                .filter(({ order }) => order === 1)
                .map((pref) => pref.preferenceIndustry)
        )
        setSecondaryIndustries(
            industryPreferences
                .filter(({ order }) => order === 2)
                .map((pref) => pref.preferenceIndustry)
        )
        setTertiaryIndustries(
            industryPreferences
                .filter(({ order }) => order === 3)
                .map((pref) => pref.preferenceIndustry)
        )
    }, [
        industryPreferences,
        setPrimaryIndustries,
        setSecondaryIndustries,
        setTertiaryIndustries,
    ])

    const isEdit = useMemo(
        () =>
            editArray.find((ele) => ele.sectionName === sectionName)?.isEdit ||
            false,
        [editArray, sectionName]
    )

    // update formValues state if new data comes back from the API
    useEffect(() => {
        resetForm()
    }, [industryPreferences, resetForm])

    // reset form when user cancels edit
    useEffect(() => {
        if (!isEdit) resetForm()
    }, [isEdit, resetForm])

    const unsavedChangesExist = useMemo(() => {
        if (!industryPreferences) return false
        const primaryFromDb = industryPreferences
            .filter(({ order }) => order === 1)
            .map((pref) => pref.preferenceIndustry.id)
        const secondaryFromDb = industryPreferences
            .filter(({ order }) => order === 2)
            .map((pref) => pref.preferenceIndustry.id)
        const tertiaryFromDb = industryPreferences
            .filter(({ order }) => order === 3)
            .map((pref) => pref.preferenceIndustry.id)
        if (
            primaryIndustries.length !== primaryFromDb.length ||
            secondaryIndustries.length !== secondaryFromDb.length ||
            tertiaryIndustries.length !== tertiaryFromDb.length ||
            primaryIndustries.map((ind) => ind.id).join() !==
                primaryFromDb.join() ||
            secondaryIndustries.map((ind) => ind.id).join() !==
                secondaryFromDb.join() ||
            tertiaryIndustries.map((ind) => ind.id).join() !==
                tertiaryFromDb.join()
        ) {
            return true
        } else {
            return false
        }
    }, [
        industryPreferences,
        primaryIndustries,
        secondaryIndustries,
        tertiaryIndustries,
    ])

    useEffect(() => {
        setUnsavedChangesExist(unsavedChangesExist)
    }, [unsavedChangesExist, setUnsavedChangesExist])

    // reset form when we cancel out of editing
    useEffect(() => {
        if (!isEdit) resetForm()
    }, [isEdit, resetForm])

    const handleUpdate = async () => {
        try {
            const { data: mutationResult, errors } = await mutate({
                variables: {
                    candidateId: candidate.id,
                    preferences: [
                        ...primaryIndustries.map((industry) => ({
                            preferenceIndustryId: industry.id,
                            order: 1,
                        })),
                        ...secondaryIndustries.map((industry) => ({
                            preferenceIndustryId: industry.id,
                            order: 2,
                        })),
                        ...tertiaryIndustries.map((industry) => ({
                            preferenceIndustryId: industry.id,
                            order: 3,
                        })),
                    ],
                },
            })
            if (mutationResult?.updateCandidateIndustryPreferences.success) {
                handleEditArray(sectionName, true)
                toast({
                    severity: 'success',
                    title: 'Success',
                    message: 'Your industry preferences have been saved',
                })
                refetch()
                setUnsavedChangesExist(false)
            } else {
                console.log(errors)
                toast({
                    severity: 'error',
                    title: 'Error',
                    message: 'Failed to update industry preferences',
                })
            }
        } catch (error) {
            console.log(error)
            toast({
                severity: 'error',
                title: 'Error',
                message: 'Failed to update industry preferences',
            })
        }
    }

    return (
        <>
            <PreferenceSectionHeader
                handleEditArray={handleEditArray}
                sectionName={sectionName}
                title="Industry"
                subTitle="What industries are you interested in working in?"
            />
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <ToggleEditMultiSelectField
                        edit={isEdit}
                        label="Primary Preference"
                        labelId="industry_primary_preference"
                        dropdownData={
                            IndustriesOptions.data?.preferenceIndustries.filter(
                                (industry) =>
                                    secondaryIndustries
                                        .concat(tertiaryIndustries)
                                        .map((industry) => industry.id)
                                        .indexOf(industry.id) === -1
                            ) || []
                        }
                        isDisabled={false}
                        onInputChange={(selectedOptions) =>
                            setPrimaryIndustries(selectedOptions)
                        }
                        selectedDropdownData={primaryIndustries}
                        limit={3}
                    />
                </Grid>
                <Grid item xs={6}>
                    <ToggleEditMultiSelectField
                        edit={isEdit}
                        label="Secondary Preference"
                        labelId="industry_secondary_preference"
                        isDisabled={false}
                        dropdownData={
                            IndustriesOptions.data?.preferenceIndustries.filter(
                                (industry) =>
                                    primaryIndustries
                                        .concat(tertiaryIndustries)
                                        .map((industry) => industry.id)
                                        .indexOf(industry.id) === -1
                            ) || []
                        }
                        selectedDropdownData={secondaryIndustries}
                        onInputChange={(selectedOptions) =>
                            setSecondaryIndustries(selectedOptions)
                        }
                        limit={3}
                    />
                </Grid>
                <Grid item xs={6}>
                    <ToggleEditMultiSelectField
                        edit={isEdit}
                        label="Tertiary Preference"
                        labelId="industry_tertiary_preference"
                        isDisabled={false}
                        dropdownData={
                            IndustriesOptions.data?.preferenceIndustries.filter(
                                (industry) =>
                                    secondaryIndustries
                                        .concat(primaryIndustries)
                                        .map((industry) => industry.id)
                                        .indexOf(industry.id) === -1
                            ) || []
                        }
                        selectedDropdownData={tertiaryIndustries}
                        onInputChange={(selectedOptions) =>
                            setTertiaryIndustries(selectedOptions)
                        }
                        limit={3}
                    />
                </Grid>
                {isEdit && (
                    <EditButtonContainer item xs={12}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                handleEditArray(sectionName, true)
                                resetForm()
                                setUnsavedChangesExist(false)
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate}>Save and Update</Button>
                    </EditButtonContainer>
                )}
            </Grid>
        </>
    )
}

export default Industry
